import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Clock, Check, Hammer, TrendingUp, Lightbulb, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const statusClass = { Live: "status-live", MVP: "status-mvp", Idea: "status-idea" };

export default function DashboardPage() {
  const { user, api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="spinner"></div></div>;

  return (
    <main className="min-h-screen bg-slate-50 pt-24 md:pt-28 pb-24" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-400 mb-2">Dashboard</p>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
            Welcome, <span className="gradient-text">{user?.name?.split(" ")[0]}</span>
          </h1>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <Link to="/portfolio" data-testid="dash-explore-link">
            <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-blue-300 transition-all duration-300 cursor-pointer">
              <ArrowRight className="w-5 h-5 text-blue-500 mb-2" />
              <p className="font-sans text-sm font-medium text-slate-900">Explore Portfolio</p>
              <p className="font-sans text-xs text-slate-400 mt-1">Browse all ventures</p>
            </div>
          </Link>
          <Link to="/ideas" data-testid="dash-ideas-link">
            <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-blue-300 transition-all duration-300 cursor-pointer">
              <Lightbulb className="w-5 h-5 text-amber-500 mb-2" />
              <p className="font-sans text-sm font-medium text-slate-900">Idea Box</p>
              <p className="font-sans text-xs text-slate-400 mt-1">Share & discover ideas</p>
            </div>
          </Link>
          <Link to="/profile" data-testid="dash-profile-link">
            <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-blue-300 transition-all duration-300 cursor-pointer">
              <div className="w-5 h-5 bg-gradient-to-r from-blue-600 to-cyan-400 rounded-full mb-2"></div>
              <p className="font-sans text-sm font-medium text-slate-900">Your Profile</p>
              <p className="font-sans text-xs text-slate-400 mt-1">Edit your information</p>
            </div>
          </Link>
        </div>

        <div className="mb-10">
          <h2 className="font-serif text-2xl font-bold text-slate-900 mb-4">My Projects</h2>
          {data?.projects?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.projects.map((project) => (
                <Link key={project.project_id} to={`/project/${project.project_id}`} data-testid={`dash-project-${project.project_id}`}>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-blue-300 transition-all duration-300"
                    style={{ borderLeftWidth: "3px", borderLeftColor: project.brand_color }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-serif text-lg font-bold text-slate-900">{project.name}</h3>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${statusClass[project.status]}`}>{project.status}</span>
                        <Badge variant="outline" className="text-[10px] capitalize gap-1">
                          {project.user_role === "builder" && <Hammer className="w-3 h-3" />}
                          {project.user_role === "investor" && <TrendingUp className="w-3 h-3" />}
                          {project.user_role}
                        </Badge>
                      </div>
                    </div>
                    <p className="font-sans text-sm text-slate-500">{project.tagline}</p>
                  </motion.div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
              <p className="font-sans text-sm text-slate-400 mb-3">You haven't joined any projects yet.</p>
              <Link to="/portfolio">
                <Button variant="outline" size="sm" className="font-sans border-slate-200" data-testid="dash-explore-projects-btn">Explore Portfolio</Button>
              </Link>
            </div>
          )}
        </div>

        {data?.pending_requests?.length > 0 && (
          <div>
            <h2 className="font-serif text-2xl font-bold text-slate-900 mb-4">Pending Requests</h2>
            <div className="space-y-3">
              {data.pending_requests.map((req) => (
                <div key={req.request_id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4" data-testid={`pending-req-${req.request_id}`}>
                  <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-sans text-sm text-slate-900">
                      <span className="font-medium capitalize">{req.type}</span> request for <span className="font-medium">{req.project_name}</span>
                    </p>
                    <p className="font-mono text-xs text-slate-400 mt-0.5">Submitted {new Date(req.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50 text-xs">Pending</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
